import { useCopilotConversations } from '../components/session-provider';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { useWorkspaceIdFromCurrentUrl } from '@/services/router/use-workspace-id';
import { buildAiFrom } from '../utils/build-ai-from';
import { useCallback } from 'react';
import { useRouter } from '@tanstack/react-router';
import { useLocation } from '@/services/router/compat';

const AGENT_CHAT_PATTERN = /\/workspaces\/\d+\/agent\/?$/;

/**
 * Returns a function that navigates to the agent chat page.
 * Optionally resets the session and sends a message.
 *
 * Skips navigation when already on the agent chat page to
 * avoid pushing duplicate entries onto the browser history stack.
 */
export function useNavigateToAgentChat() {
  const router = useRouter();
  const { newConversation, switchToConversation } = useCopilotConversations();
  const navigate = useSafeNavigate();
  const workspaceId = useWorkspaceIdFromCurrentUrl() ?? 0;
  const { pathname } = useLocation();

  return useCallback(
    (options?: { message?: string; sessionId?: string }) => {
      if (options?.sessionId != null) {
        switchToConversation(options.sessionId);
      } else {
        const newSession = newConversation();
        if (options?.message != null) {
          void newSession.sendMessage(options.message);
        }
      }

      if (!AGENT_CHAT_PATTERN.test(pathname)) {
        navigate(
          router.buildLocation({
            to: '/workspaces/$workspaceId/agent',
            params: { workspaceId },
            search: { 'ai-from': buildAiFrom() },
          }).href,
        );
      }
    },
    [newConversation, switchToConversation, navigate, workspaceId, pathname, router],
  );
}
